import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import store, { showToast, showToastWithType } from '../../../../core/store';
import {
  archiveCasePlanSection,
  createCasePlanSection,
  deleteCasePlanSection,
  moveCasePlanSection,
  updateCasePlanSection,
} from '../../api/casePlanDetailApi';
import {
  CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS,
  CASE_PLAN_SECTION_ERROR_MESSAGES,
  CASE_PLAN_SECTION_SUCCESS_MESSAGES,
} from '../../constants/reentry-planning.constants';
import {
  isStaleSectionStateError,
  resolveSectionErrorMessage,
} from '../../utils/case-plan-section-error.util';
import {
  applySectionFields,
  buildPendingSection,
  findSectionById,
  findSectionByKey,
  findSectionIndexByKey,
  getSectionKey,
  insertSectionBefore,
  isDeletedSection,
  isPendingSection,
  mergeSectionInto,
  mergeSectionResponse,
  moveSectionInDirection,
  pickSectionFields,
  preserveSectionDisplayFields,
  reinsertSectionAt,
  removeSectionByKey,
  resolveInsertBeforeSectionId,
  resolveSectionMoveSortOrder,
  revertSectionMove,
  settlePendingSection,
  toSectionApiFields,
} from '../../utils/case-plan-section.util';
import { mapSectionMutationResponse } from '../../utils/reentry-plan-detail.util';
import { usePendingSectionCreates } from './usePendingSectionCreates';
import { useSectionCreateQueue } from './useSectionCreateQueue';

/**
 * The two destructive requests and what a *successful* response means for the row, which has
 * already left the list by then. Delete is terminal — the section is not listed by the API
 * again and there is no tab it could return to — so `dropOnSuccess` keeps it gone. Archive
 * puts its response back, where the archived status lists it under Archived.
 */
const REMOVAL_REQUESTS = {
  [CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.DELETE]: {
    call: deleteCasePlanSection,
    dropOnSuccess: true,
    successMessage: CASE_PLAN_SECTION_SUCCESS_MESSAGES.DELETE,
    errorMessage: CASE_PLAN_SECTION_ERROR_MESSAGES.DELETE,
  },
  [CASE_PLAN_SECTION_DESTRUCTIVE_ACTIONS.ARCHIVE]: {
    call: archiveCasePlanSection,
    dropOnSuccess: false,
    successMessage: CASE_PLAN_SECTION_SUCCESS_MESSAGES.ARCHIVE,
    errorMessage: CASE_PLAN_SECTION_ERROR_MESSAGES.ARCHIVE,
  },
};

const normalizeText = (value) => String(value ?? '').trim();

const hasFields = (fields) => Object.keys(fields).length > 0;

/**
 * Whether a removed row goes back into the list once its request succeeds. The response status
 * is checked alongside the action config so a section the API reports as deleted stays gone
 * whichever endpoint returned it.
 */
const shouldKeepSectionRemoved = (request, response) =>
  request.dropOnSuccess || isDeletedSection(response);

/**
 * Persists every section mutation, optimistically: the list is updated the moment the admin
 * acts and the request runs behind it. Add Section inserts a pending placeholder row and
 * Delete/Archive removes the row before either request is sent, so neither action waits on
 * the network.
 *
 * A successful Archive puts its row back so it appears under Archived; a successful Delete
 * does not, because the section is terminally gone.
 *
 * A rejected mutation is rolled back **surgically** — only the fields, the row, or the swap it
 * changed — because unrelated sections may have been edited while it was in flight. A 404
 * additionally reloads the plan, since local state is then stale.
 *
 * @param {{
 *   casePlanId: string,
 *   sections: object[],
 *   setSections: (sections: object[] | ((current: object[]) => object[])) => void,
 *   reloadDetail: () => void,
 * }} params
 * @returns {object} Section mutation handlers plus `newSectionKey`, the section whose title
 *   should open focused and selected.
 */
export const useCasePlanSectionMutations = ({
  casePlanId,
  sections,
  setSections,
  reloadDetail,
}) => {
  const [newSectionKey, setNewSectionKey] = useState(null);

  const {
    createClientKey,
    registerPendingCreate,
    isPendingCreate,
    queuePendingFields,
    requestPendingRemoval,
    settlePendingCreate,
    resolveSectionId,
  } = usePendingSectionCreates();

  const { enqueueSectionCreate } = useSectionCreateQueue();

  // The list also changes from outside this hook (the initial load and every reloadDetail).
  // Syncing before paint keeps the ref usable by a handler that fires right after a reload.
  const sectionsRef = useRef(sections);
  useLayoutEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  /**
   * Single entry point for every local list write. The transform runs *inside* the state
   * updater, so it always composes on React's current list: a request that resolves just after
   * the plan reloaded merges into the freshly loaded sections instead of replacing them with
   * the snapshot it captured before its `await`. The ref is advanced with it so the next
   * handler reads the same list.
   */
  const updateSections = useCallback(
    (updateList) => {
      setSections((current) => {
        const nextSections = updateList(current);

        sectionsRef.current = nextSections;

        return nextSections;
      });
    },
    [setSections],
  );

  const handleFailure = useCallback(
    (error, errorMessage, rollbackSections = null) => {
      if (rollbackSections) {
        updateSections(rollbackSections);
      }

      store.dispatch(showToast(resolveSectionErrorMessage(error, errorMessage)));

      if (isStaleSectionStateError(error)) {
        reloadDetail();
      }
    },
    [reloadDetail, updateSections],
  );

  /** Resolves a possibly-temporary id against the live list, so late handlers still match. */
  const findLiveSection = useCallback(
    (sectionId) => findSectionById(sectionsRef.current, resolveSectionId(sectionId)),
    [resolveSectionId],
  );

  /**
   * Applies edited fields locally, then persists them. A section whose create has not answered
   * yet has no server id, so its fields are queued instead and flushed once it does — a
   * temporary id is never sent to the API.
   *
   * @param {string} sectionId - Section id, or the client key of a section still being created.
   * @param {object} uiFields - Edited UI fields, applied on screen and sent as the patch.
   * @param {string} errorMessage - Toast copy for a rejected patch.
   * @param {object | null} rollbackFields - What a rejected patch reverts to. Defaults to the
   *   values currently on screen; flushing edits queued during a create must pass the values
   *   the server persisted instead, because the queued ones are already painted and reverting
   *   to them would leave a rejected edit looking saved.
   */
  const patchSection = useCallback(
    async (sectionId, uiFields, errorMessage, rollbackFields = null) => {
      const targetId = resolveSectionId(sectionId);
      const previousFields =
        rollbackFields ?? pickSectionFields(findLiveSection(sectionId), uiFields);

      updateSections((current) => applySectionFields(current, targetId, uiFields));

      if (isPendingCreate(sectionId)) {
        queuePendingFields(sectionId, uiFields);
        return;
      }

      try {
        const response = await updateCasePlanSection(
          casePlanId,
          targetId,
          toSectionApiFields(uiFields),
        );
        updateSections((current) =>
          mergeSectionResponse(current, mapSectionMutationResponse(response)),
        );
      } catch (error) {
        handleFailure(error, errorMessage, (current) =>
          applySectionFields(current, targetId, previousFields),
        );
      }
    },
    [
      casePlanId,
      findLiveSection,
      handleFailure,
      isPendingCreate,
      queuePendingFields,
      resolveSectionId,
      updateSections,
    ],
  );

  /**
   * @returns {Promise<boolean>} False when the title was rejected, so the editor can restore
   *   the persisted value. A blank title is never sent and never changes local state.
   */
  const saveSectionTitle = useCallback(
    async (sectionId, title) => {
      const nextTitle = normalizeText(title);

      if (nextTitle === '') {
        store.dispatch(showToast(CASE_PLAN_SECTION_ERROR_MESSAGES.BLANK_TITLE));
        return false;
      }

      if (findLiveSection(sectionId)?.title !== nextTitle) {
        await patchSection(
          sectionId,
          { title: nextTitle },
          CASE_PLAN_SECTION_ERROR_MESSAGES.UPDATE,
        );
      }

      return true;
    },
    [findLiveSection, patchSection],
  );

  const saveSectionDescription = useCallback(
    async (sectionId, description) => {
      const nextDescription = normalizeText(description);

      if (findLiveSection(sectionId)?.description === nextDescription) {
        return;
      }

      await patchSection(
        sectionId,
        { description: nextDescription },
        CASE_PLAN_SECTION_ERROR_MESSAGES.UPDATE,
      );
    },
    [findLiveSection, patchSection],
  );

  const saveSectionVisibility = useCallback(
    (sectionId, visibleToLearner) =>
      patchSection(sectionId, { visibleToLearner }, CASE_PLAN_SECTION_ERROR_MESSAGES.UPDATE),
    [patchSection],
  );

  /**
   * Sends the destructive request for a section already gone from the UI. Archive puts the
   * response back at `index`, where its archived status keeps it off the Active tab and lists
   * it under Archived; Delete leaves the row gone, because a deleted section is terminal and
   * belongs to no tab. Either way the admin stays on Active, so the success toast is what
   * confirms the section went where it was supposed to.
   */
  const sendSectionRemoval = useCallback(
    async (section, action, index) => {
      const request = REMOVAL_REQUESTS[action];

      try {
        const response = mapSectionMutationResponse(await request.call(casePlanId, section.id));

        if (!shouldKeepSectionRemoved(request, response)) {
          const archivedSection = preserveSectionDisplayFields(response, section);
          updateSections((current) => mergeSectionInto(current, archivedSection, index));
        }

        store.dispatch(showToastWithType(request.successMessage, 'success'));
      } catch (error) {
        handleFailure(error, request.errorMessage, (current) =>
          reinsertSectionAt(current, section, index),
        );
      }
    },
    [casePlanId, handleFailure, updateSections],
  );

  /**
   * Settles a create: the placeholder becomes the created section, then whatever happened
   * while the request was in flight is applied to the real id — a removal confirmed early is
   * finally sent, otherwise queued field edits are flushed as a single POST.
   */
  const applyCreatedSection = useCallback(
    async (clientKey, createdSection) => {
      const { queuedFields, removalAction, removalIndex } = settlePendingCreate(
        clientKey,
        createdSection.id,
      );

      updateSections((current) =>
        settlePendingSection(current, clientKey, createdSection, queuedFields),
      );

      // The title opened focused when the row was inserted, and `titleAutoFocus` is read once
      // on mount. Releasing the key here stops a later remount of the same row — switching to
      // Archived and back — from reopening its title editor. A newer pending section keeps its
      // own focus key.
      setNewSectionKey((current) => (current === clientKey ? null : current));

      if (removalAction) {
        await sendSectionRemoval({ ...createdSection, clientKey }, removalAction, removalIndex);
        return;
      }

      if (hasFields(queuedFields)) {
        await patchSection(
          createdSection.id,
          queuedFields,
          CASE_PLAN_SECTION_ERROR_MESSAGES.UPDATE,
          // The queued values are already on screen, so a rejected flush has to fall back to
          // what the create actually persisted.
          pickSectionFields(createdSection, queuedFields),
        );
      }
    },
    [patchSection, sendSectionRemoval, settlePendingCreate, updateSections],
  );

  /** A rejected create takes only its own placeholder row — and its focus — with it. */
  const discardPendingSection = useCallback(
    (clientKey, error) => {
      settlePendingCreate(clientKey);
      setNewSectionKey((current) => (current === clientKey ? null : current));

      handleFailure(error, CASE_PLAN_SECTION_ERROR_MESSAGES.CREATE, (current) =>
        removeSectionByKey(current, clientKey),
      );
    },
    [handleFailure, settlePendingCreate],
  );

  const runSectionCreate = useCallback(
    async (clientKey) => {
      const insertBeforeSectionId = resolveInsertBeforeSectionId(sectionsRef.current, clientKey);

      try {
        const response = await createCasePlanSection(casePlanId, { insertBeforeSectionId });
        await applyCreatedSection(clientKey, mapSectionMutationResponse(response));
      } catch (error) {
        discardPendingSection(clientKey, error);
      }
    },
    [applyCreatedSection, casePlanId, discardPendingSection],
  );

  /**
   * Add Section inserts the placeholder row and focuses its title synchronously; the request
   * runs behind it. Creates are queued so their anchors resolve in click order.
   *
   * @param {string | null} insertBeforeSectionKey - Key of the section the new one lands above;
   *   null appends (the trailing control and the empty plan).
   */
  const addSection = useCallback(
    (insertBeforeSectionKey = null) => {
      const clientKey = createClientKey();

      registerPendingCreate(clientKey);
      updateSections((current) =>
        insertSectionBefore(current, buildPendingSection(clientKey), insertBeforeSectionKey),
      );
      setNewSectionKey(clientKey);

      return enqueueSectionCreate(() => runSectionCreate(clientKey));
    },
    [
      createClientKey,
      enqueueSectionCreate,
      registerPendingCreate,
      runSectionCreate,
      updateSections,
    ],
  );

  const moveSection = useCallback(
    async (sectionId, direction) => {
      const currentSections = sectionsRef.current;
      const targetSectionId = resolveSectionId(sectionId);

      // The arrows are disabled while a create is in flight; this is the API-side guarantee
      // that a temporary id never reaches the move endpoint.
      if (isPendingCreate(sectionId)) {
        return;
      }

      const reorderedSections = moveSectionInDirection(currentSections, sectionId, direction);

      // Same reference means the section is already at the boundary — nothing to persist.
      if (reorderedSections === currentSections) {
        return;
      }

      const sortOrder = resolveSectionMoveSortOrder(reorderedSections, targetSectionId);

      if (sortOrder === null) {
        return;
      }

      updateSections(() => reorderedSections);

      try {
        const response = await moveCasePlanSection(casePlanId, targetSectionId, sortOrder);
        updateSections((current) =>
          mergeSectionResponse(current, mapSectionMutationResponse(response)),
        );
      } catch (error) {
        handleFailure(error, CASE_PLAN_SECTION_ERROR_MESSAGES.MOVE, (current) =>
          revertSectionMove(current, sectionId, direction),
        );
      }
    },
    [casePlanId, handleFailure, isPendingCreate, resolveSectionId, updateSections],
  );

  /**
   * Runs the confirmed destructive action. The row leaves the list before the request is sent:
   * a section the admin just confirmed away must not linger. A section whose create has not
   * answered yet still disappears, and its removal is deferred until a real id exists.
   */
  const removeSection = useCallback(
    (confirmedSection, action) => {
      const sectionKey = getSectionKey(confirmedSection);
      const currentSections = sectionsRef.current;
      // Re-read the section: the confirmed copy may predate its create settling.
      const section = findSectionByKey(currentSections, sectionKey);
      const index = findSectionIndexByKey(currentSections, sectionKey);

      if (!REMOVAL_REQUESTS[action] || !section) {
        return Promise.resolve();
      }

      updateSections((current) => removeSectionByKey(current, sectionKey));

      if (isPendingSection(section)) {
        requestPendingRemoval(sectionKey, action, index);
        return Promise.resolve();
      }

      return sendSectionRemoval(section, action, index);
    },
    [requestPendingRemoval, sendSectionRemoval, updateSections],
  );

  return {
    newSectionKey,
    addSection,
    saveSectionTitle,
    saveSectionDescription,
    saveSectionVisibility,
    moveSection,
    removeSection,
  };
};
